<script setup lang="ts">
// get params
const route = useRoute()
const params = route.params

// get employer
const employerId = params.id as string

const { getCompany } = useCompany()
const { listJobOnce } = useJob()

const employer = await getCompany(employerId)

if (!employer) {
  throw createError({
    statusCode: 404,
    message: 'Employer not found',
    fatal: true
  })
}

// config
const runtimeConfig = useRuntimeConfig()

const siteUrl = runtimeConfig.public.siteUrl
const pageUrl = `${siteUrl}${route.path}`
const ogTitle = `${employer.name} | ${runtimeConfig.public.siteName}`
const ogDescription = 'View more hiring partners from TalentLabs'

useSeoMeta({
  title: () => ogTitle,
  description: () => ogDescription,
  ogSiteName: () => ogTitle,
  ogTitle: () => ogTitle,
  ogDescription: () => ogDescription,
  ogUrl: () => pageUrl,
  // ogImageUrl: () => ogImageUrl,
  ogImageHeight: 630,
  ogImageWidth: 1200,
  ogImageAlt: () => ogTitle,
  ogImageType: 'image/png',
  // ogImageSecureUrl: () => ogImageUrl,
  twitterCard: 'summary_large_image',
  twitterTitle: ogTitle,
  twitterDescription: () => ogDescription
  // twitterImage: () => ogImageUrl
})

definePageMeta({
  layout: 'landing'
})

// get 3 jobs from employers
const jobs = (
  await listJobOnce({
    company__id_slug: [employerId],
    page_size: 3
  })
).data

// REF
const active = ref(0)

// tabs
const tabs = [
  {
    title: 'Overview',
    icon: defineAsyncComponent(
      () => import('~/components/common/icon/employers/IconEmpOverview.vue')
    )
  },
  {
    title: 'Jobs',
    icon: defineAsyncComponent(() => import('~/components/common/icon/employers/IconEmpJobs.vue'))
  }
]
</script>

<template>
  <div id="employer">
    <div id="employer-header">
      <div class="container">
        <div class="breadcrumbs">
          <NuxtLink to="/">Home</NuxtLink>
          <b>></b>
          <NuxtLink to="/jobs?page=1">Jobs</NuxtLink>
          <b>></b>
          <NuxtLink :to="$route.path">{{ employer.name }} </NuxtLink>
        </div>

        <div id="employer-header-banner">
          <img
            v-if="employer.banner_part"
            :src="toCompanyIconUrl(employer.banner_part?.object_key)"
            :alt="employer.name"
          >
        </div>

        <img
          v-if="employer.logo_part"
          id="employer-header-logo"
          :src="toCompanyIconUrl(employer.logo_part?.object_key)"
          :alt="employer.name"
        >

        <div v-else id="employer-header-logo-default">
          <CommonIconJobsIconDefaultCompany />
        </div>

        <h1>{{ employer.name }}</h1>
        <!-- TODO Add short name to backend apis -->
        <!-- <h2 v-if="employer.shortTitle !== employer.title">
          {{ employer.title }}
        </h2> -->
      </div>
    </div>

    <div id="employer-bar">
      <div class="container">
        <div
          v-for="(tab, i) in tabs"
          :key="tab.title"
          :class="`bar-item clickable ${active === i ? 'active' : ''}`"
          @click="active = i"
        >
          <component :is="tab.icon" />
          {{ tab.title }}
        </div>
      </div>
    </div>

    <div id="employer-content">
      <div class="container">
        <div v-if="active === 0" id="employer-overview">
          <div v-if="employer.location" class="employer-detail">
            <CommonIconJobsIconLocationOutline />
            <span>{{ getReadableJobLocation(employer.location) }}</span>
          </div>

          <div v-if="employer.industry" class="employer-detail">
            <CommonIconJobsIconBuilding />
            <span>{{ getReadableJobIndustry(employer.industry) }}</span>
          </div>

          <div v-if="employer.scale" class="employer-detail">
            <CommonIconJobsIconPeople />
            <span>{{ employer.scale }}</span>
          </div>

          <div v-if="employer.website" class="employer-detail">
            <CommonIconJobsIconWorld />

            <a :href="employer.website" target="_blank">
              {{ employer.website }}
            </a>
          </div>

          <div>
            <h6>About {{ employer.name }}</h6>
            <div v-html="employer.description"/>
          </div>
        </div>

        <div v-if="active === 1 && jobs.length > 0" id="employer-jobs">
          <PageJobsVerticalJobCard v-for="job in jobs" :key="job.id_slug" :job="job" />

          <div id="employer-jobs-btn">
            <CommonButton :large="true" href="/jobs?page=1"> See more jobs </CommonButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
#employer {
  padding-bottom: 90px;
  position: relative;

  .container {
    width: 850px;
    margin: auto;
  }

  &-header {
    display: flex;
    flex-direction: column;
    text-align: center;
    justify-content: center;

    .breadcrumbs {
      margin: 30px 0 20px;
    }

    &-banner {
      width: 100%;
      height: 180px;
      border-radius: 15px;
      overflow: hidden;
      background-color: var(--lightBlue);
      background: linear-gradient(132deg, rgba(249, 238, 255, 1) 0%, rgba(219, 242, 255, 1) 100%);

      img {
        height: 100%;
        width: 100%;
        object-fit: cover;
      }
    }

    $size: 120px;

    &-logo-default,
    &-logo {
      height: $size;
      max-width: calc($size * 1.7);
      border-radius: 25px;
      margin: auto;
      margin-top: -80px;
      object-fit: contain;
      background-color: white;
    }

    &-logo {
      padding: 12px;
    }

    &-logo-default {
      width: $size;
      border: 12px solid white;
      overflow: hidden;

      svg {
        height: 100%;
        width: 100%;
      }
    }

    h1 {
      font-size: 1.7rem;
      line-height: 2.2rem;
      margin-top: 3px;
    }

    h2 {
      margin-top: 4px;
      font-size: 1.1rem;
      font-weight: 400;
      line-height: 1.7rem;
    }
  }

  &-bar {
    margin: 55px 0 50px;
    position: sticky;
    top: var(--navbarHeight);
    left: 0;
    background-color: white;
    z-index: 5;

    .container {
      display: flex;
      border-bottom: 1px solid gainsboro;
    }

    .bar-item {
      padding: 13px 20px;
      font-weight: 500;
      font-size: 0.95rem;
      color: var(--textLight);
      margin-right: 25px;
      display: flex;
      align-items: center;
      border-bottom: 3px solid transparent;

      svg {
        $iconSize: 16px;
        height: $iconSize;
        width: $iconSize;
        margin-right: 9px;
        fill: var(--textLight);
      }
    }

    .active {
      border-color: var(--purple);
      color: var(--purple);

      svg {
        fill: var(--purple);
      }
    }
  }

  &-content {
    .container {
      width: 750px;
    }

    h2 {
      font-size: 1.4rem;
      margin-bottom: 40px;
    }
  }

  &-overview {
    .employer-detail {
      margin-bottom: 17px;
      display: flex;
      align-items: center;

      svg {
        $iconSize: 17px;
        height: $iconSize;
        width: $iconSize;
        margin-right: 20px;
      }
    }

    h6 {
      padding: 40px 0 25px;
      font-size: 0.95rem;
      color: var(--textLight);
    }

    p {
      font-size: 1.1rem;
      line-height: 1.7rem;
    }
  }

  &-jobs {
    margin: auto;

    &-btn {
      margin: 35px auto 0;
      text-align: center;
    }
  }
}

@media (max-width: 900px) {
  #employer {
    .container {
      width: 100%;
      padding: 0 20px;
    }
  }
}

@media (max-width: 600px) {
  #employer {
    &-header {
      .breadcrumbs {
        margin: 20px 0 25px;
      }

      &-banner {
        height: 110px;
        margin-left: -20px;
        width: 100vw;
        border-radius: 0;
      }

      $size: 80px;

      &-logo-default,
      &-logo {
        height: $size;
        max-width: calc($size * 1.7);
        margin-top: -60px;
        border-radius: 20px;
      }

      &-logo {
        padding: 10px;
      }

      &-logo-default {
        width: $size;
        border-width: 10px;
      }

      h1 {
        font-size: 1.3rem;
        line-height: 1.8rem;
      }

      h2 {
        font-size: 0.95rem;
      }
    }

    &-bar {
      margin: 25px 0 35px;

      .bar-item {
        padding: 20px 17px 15px;
        font-size: 0.9rem;
        margin-right: 15px;

        svg {
          $size: 14px;
          height: $size;
          width: $size;
          margin-right: 7px;
        }
      }
    }

    &-overview {
      .employer-detail {
        margin-bottom: 12px;
      }
    }
  }
}

@media (hover: hover) {
  #employer {
    &-bar {
      .bar-item:hover {
        color: var(--purple);

        svg {
          fill: var(--purple);
        }
      }
    }

    &-overview {
      .employer-detail {
        a:hover {
          color: var(--purple);
        }
      }
    }
  }
}
</style>
